/**
 * Shared API client for Atlas Casino REST endpoints.
 *
 * API Gateway methods use AuthorizationType=AWS_IAM. We sign every request
 * with SigV4 using the Cognito Identity Pool credentials that `fetchAuthSession`
 * exchanges from the User Pool tokens.
 *
 * Why not `aws-amplify/api`'s `get`/`post`? In Amplify v6, the REST API
 * client does not reliably auto-sign requests when the legacy
 * `aws_cloud_logic_custom` config is used. Even with the v6 `service:
 * 'execute-api'` config we observed unsigned requests (403 Missing
 * Authentication Token). Signing here gives us explicit control.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';
import toast from 'react-hot-toast';
import awsExports from '../aws-exports.js';

// Paths whose errors should NOT surface as toasts (e.g. polling endpoints
// that legitimately 404 / 401 during sign-in races). Match by `startsWith`.
const SILENT_ERROR_PREFIXES = ['/user/balance', '/user/deposits', '/user/withdrawals', '/user/attribution'];

function shouldSurfaceError(path, status) {
  if (status === 401 || status === 403) return false; // auth flicker
  return !SILENT_ERROR_PREFIXES.some((p) => path.startsWith(p));
}

const endpointCfg = awsExports?.aws_cloud_logic_custom?.[0];
const REGION = endpointCfg?.region || awsExports?.aws_project_region;
const ENDPOINT_URL = new URL(endpointCfg?.endpoint);

async function signedFetch(method, path, { body, queryParams } = {}) {
  const session = await fetchAuthSession();
  const creds = session?.credentials;
  if (!creds) throw new Error('Not signed in (no Cognito Identity Pool credentials)');

  const queryString = buildQuery(queryParams);
  const pathWithBase = `${ENDPOINT_URL.pathname.replace(/\/$/, '')}${path}`;
  const headers = {
    host: ENDPOINT_URL.host,
    ...(body ? { 'content-type': 'application/json' } : {}),
  };
  const bodyStr = body !== undefined ? JSON.stringify(body) : undefined;

  const queryObj = {};
  if (queryString) {
    for (const [k, v] of new URLSearchParams(queryString)) queryObj[k] = v;
  }

  const req = new HttpRequest({
    method,
    protocol: ENDPOINT_URL.protocol,
    hostname: ENDPOINT_URL.hostname,
    path: pathWithBase,
    query: queryObj,
    headers,
    body: bodyStr,
  });

  const signer = new SignatureV4({
    credentials: {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
    },
    region: REGION,
    service: 'execute-api',
    sha256: Sha256,
    // Skip the X-Amz-Content-SHA256 header. API Gateway's CORS allowlist
    // (set by Amplify) does NOT include it, so a preflight rejects the
    // actual request when this header is present. The body hash still goes
    // into the canonical request — API Gateway recomputes from the body.
    applyChecksum: false,
  });

  const signed = await signer.sign(req);

  const url = `${ENDPOINT_URL.origin}${pathWithBase}${queryString}`;
  const response = await fetch(url, {
    method: signed.method,
    headers: signed.headers,
    body: signed.body,
  });

  const text = await response.text();
  let json;
  try { json = text ? JSON.parse(text) : {}; } catch { json = { raw: text }; }
  if (!response.ok) {
    const err = new Error(json?.error || json?.message || `HTTP ${response.status}`);
    err.status = response.status;
    err.body = json;
    if (shouldSurfaceError(path, response.status)) toast.error(err.message);
    throw err;
  }
  return json;
}

function buildQuery(params) {
  if (!params) return '';
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return pairs.length ? `?${pairs.join('&')}` : '';
}

export async function apiGet(path, queryParams) {
  return signedFetch('GET', stripQuery(path), {
    queryParams: queryParams || parseInlineQuery(path),
  });
}

export async function apiPost(path, body) {
  return signedFetch('POST', stripQuery(path), { body });
}

export async function apiPut(path, body) {
  return signedFetch('PUT', stripQuery(path), { body });
}

export async function apiDelete(path, body) {
  return signedFetch('DELETE', stripQuery(path), { body });
}

// Some callers pass path with a pre-built query string (e.g. legacy
// `/user/balance?chainId=8453`). Split it so the signer can canonicalize
// the query params correctly.
function stripQuery(path) {
  const i = path.indexOf('?');
  return i === -1 ? path : path.slice(0, i);
}

function parseInlineQuery(path) {
  const i = path.indexOf('?');
  if (i === -1) return undefined;
  const out = {};
  for (const [k, v] of new URLSearchParams(path.slice(i + 1))) out[k] = v;
  return out;
}
